import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, CheckCircle, RefreshCw, Database, 
  Search, ArrowRight, UploadCloud, Smartphone, 
  CreditCard, AlertCircle, ScanLine, Brain, Sparkles, Image as ImageIcon,
  FileCheck, ShieldCheck
} from 'lucide-react';
import { Badge } from '../../ui/badge';

const ArielSlide = () => {
  const [processingStep, setProcessingStep] = useState(0);
  const [activeClaim, setActiveClaim] = useState(mockClaims[0]);

  // Simulate the processing loop
  useEffect(() => {
    const interval = setInterval(() => {
      setProcessingStep((prev) => {
         if (prev >= 3) return 0;
         return prev + 1;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Reset/Rotate claim when loop restarts
  useEffect(() => {
      if (processingStep === 0) {
          setActiveClaim(prev => {
              const idx = mockClaims.findIndex(c => c.id === prev.id);
              return mockClaims[(idx + 1) % mockClaims.length];
          });
      }
  }, [processingStep]);

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 translate-x-1/2" />

        {/* Floating Agent Header (Top Left) - Dark Theme */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/10 backdrop-blur-md p-2 pr-6 rounded-full border border-white/10 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm">
                 <img src="/assets/ariel.png" alt="Ariel" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-white leading-none">Ariel</h3>
                <p className="text-xs text-blue-400 font-bold uppercase tracking-wide">AI Claims Expert</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-8">
                
                <div className="space-y-4">
                    <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                        Perfect claim submissions with <span className="text-blue-400">auto-attached clinical evidence</span>.
                    </h2>
                    
                    <p className="text-lg text-slate-300 leading-relaxed">
                        Ariel connects deeply to your Practice Management System to generate clean claims instantly. She automatically finds and attaches the correct X-rays and clinical notes, ensuring zero rejections due to missing documentation.
                    </p>
                </div>

            </div>

            {/* Right Side: Dashboard Mockup */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="w-full h-[600px] relative">
                    {/* Background decoration */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 rounded-3xl transform rotate-3 scale-105 border border-white/5"></div>
                    
                    <motion.div 
                        initial={{ scale: 0.95, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="w-full h-full bg-slate-950 rounded-2xl shadow-2xl border border-slate-800 overflow-hidden flex flex-col relative z-10"
                    >
                        {/* Header Bar */}
                        <div className="h-14 bg-slate-900 border-b border-slate-800 flex items-center px-6 justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-bold text-slate-200">Ariel Claims Workspace</span>
                            </div>
                            <Badge variant="outline" className="border-slate-700 bg-slate-800 text-slate-400 font-mono text-xs">
                                v2.4.0 • Connected to PMS
                            </Badge>
                        </div>

                        {/* Main Content Area */}
                        <div className="flex-1 p-8 flex flex-col gap-6 relative">
                            
                            {/* Status Indicator Panel */}
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h3 className="text-2xl font-bold text-white mb-1">Processing Claim #{activeClaim.id}</h3>
                                    <p className="text-slate-400">Patient: {activeClaim.patient} • Procedure: {activeClaim.procedure}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <StatusBadge step={processingStep} />
                                </div>
                            </div>

                            {/* Dynamic Visualization Area */}
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                                
                                {/* Left: Data Gathering */}
                                <div className="bg-slate-900/50 rounded-xl border border-slate-800 p-6 flex flex-col gap-4 relative overflow-hidden">
                                    <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                        <Database className="w-4 h-4" /> PMS Data Extraction
                                    </h4>
                                    
                                    {/* Animated List of Data Points */}
                                    <div className="space-y-3">
                                        <DataPoint label="Patient Demographics" status="found" delay={0.1} active={processingStep >= 0} />
                                        <DataPoint label="Provider Info" status="found" delay={0.3} active={processingStep >= 0} />
                                        <DataPoint label="Insurance Policy" status="verified" delay={0.5} active={processingStep >= 0} />
                                        <DataPoint label="Procedure Codes" status="validated" delay={0.7} active={processingStep >= 0} />
                                    </div>

                                    {/* Scan Line Effect */}
                                    {processingStep === 0 && (
                                        <motion.div 
                                            className="absolute top-0 left-0 w-full h-1 bg-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.5)] z-10"
                                            animate={{ top: ["0%", "100%", "0%"] }}
                                            transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                                        />
                                    )}
                                </div>

                                {/* Right: Attachment Discovery */}
                                <div className="bg-slate-900/50 rounded-xl border border-slate-800 p-6 flex flex-col gap-4 relative overflow-hidden">
                                     <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                        <ImageIcon className="w-4 h-4" /> Smart Attachments
                                    </h4>

                                    {/* Attachment Grid */}
                                    <div className="grid grid-cols-2 gap-3 mt-2">
                                        {activeClaim.attachments.map((att, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ 
                                                    opacity: processingStep >= 1 ? 1 : 0.3, 
                                                    scale: processingStep >= 1 ? 1 : 0.9,
                                                    borderColor: processingStep >= 1 ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 41, 59, 1)'
                                                }}
                                                className="aspect-square bg-slate-800 rounded-lg border border-slate-700 flex flex-col items-center justify-center relative overflow-hidden group"
                                            >
                                                {att.type === 'xray' ? <ScanLine className="w-8 h-8 text-slate-600 group-hover:text-blue-400 transition-colors" /> : <FileText className="w-8 h-8 text-slate-600 group-hover:text-blue-400 transition-colors" />}
                                                <span className="text-[10px] text-slate-400 mt-2 font-mono">{att.name}</span>
                                                
                                                {/* Found Checkmark */}
                                                {processingStep >= 1 && (
                                                    <motion.div 
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg"
                                                    >
                                                        <CheckCircle className="w-3 h-3 text-white" />
                                                    </motion.div>
                                                )}
                                            </motion.div>
                                        ))}
                                    </div>
                                    
                                    {processingStep === 1 && (
                                         <div className="absolute inset-0 bg-blue-500/5 flex items-center justify-center backdrop-blur-[1px]">
                                             <div className="bg-slate-900 px-4 py-2 rounded-full border border-blue-500/30 shadow-xl flex items-center gap-2">
                                                 <RefreshCw className="w-3 h-3 text-blue-400 animate-spin" />
                                                 <span className="text-xs text-blue-200">Retrieving images...</span>
                                             </div>
                                         </div>
                                    )}
                                </div>

                            </div>

                            {/* Bottom: Submission Action */}
                            <div className="h-20 bg-slate-900/80 border-t border-slate-800 -mx-8 -mb-8 p-4 flex items-center justify-between px-8 mt-auto">
                                <div className="flex flex-col">
                                    <span className="text-xs text-slate-500 uppercase">Estimated Reimbursement</span>
                                    <span className="text-xl font-bold text-white">{activeClaim.amount}</span>
                                </div>
                                
                                <AnimatePresence mode="wait">
                                    {processingStep < 2 ? (
                                        <motion.div
                                            key="processing"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="flex items-center gap-2 text-slate-400"
                                        >
                                            <span className="text-sm">Analyzing claim details...</span>
                                        </motion.div>
                                    ) : (
                                        <motion.button
                                            key="submit"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all duration-300 ${
                                                processingStep === 3 
                                                ? 'bg-green-500 text-white shadow-lg shadow-green-500/20' 
                                                : 'bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/20'
                                            }`}
                                        >
                                            {processingStep === 3 ? (
                                                <>
                                                    <CheckCircle className="w-4 h-4" /> Claim Submitted
                                                </>
                                            ) : (
                                                <>
                                                    <ArrowRight className="w-4 h-4" /> Ready to Submit
                                                </>
                                            )}
                                        </motion.button>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    </div>
  );
};

const DataPoint = ({ label, status, delay, active }) => (
    <div className={`flex items-center justify-between p-2 rounded-lg transition-colors duration-300 ${active ? 'bg-slate-800/50' : 'bg-transparent'}`}>
        <span className={`text-sm ${active ? 'text-slate-200' : 'text-slate-600'}`}>{label}</span>
        {active ? (
            <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay }}
                className="flex items-center gap-1.5 text-xs text-green-400 font-medium"
            >
                <CheckCircle className="w-3 h-3" />
                <span className="capitalize">{status}</span>
            </motion.div>
        ) : (
             <div className="w-2 h-2 rounded-full bg-slate-700" />
        )}
    </div>
);

const StatusBadge = ({ step }) => {
    const statuses = [
        { text: "Scanning PMS", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
        { text: "Attaching Images", color: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/20" },
        { text: "Validating", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
        { text: "Submitted", color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/20" }
    ];
    const current = statuses[step] || statuses[3];
    
    return (
        <div className={`px-3 py-1 rounded-full border flex items-center gap-2 ${current.bg} ${current.border}`}>
             <div className="relative flex h-2 w-2">
                {step < 3 && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${current.color.replace('text', 'bg')}`}></span>}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${current.color.replace('text', 'bg')}`}></span>
              </div>
             <span className={`text-xs font-bold ${current.color}`}>{current.text}</span>
        </div>
    );
};

const mockClaims = [
    {
        id: "CLM-9921",
        patient: "Sarah Connor",
        procedure: "D2740 - Crown Porcelain/Ceramic",
        amount: "$1,250.00",
        attachments: [
            { name: "PA Bitewing 10/12", type: "xray" },
            { name: "Perio Chart", type: "doc" },
            { name: "Clinical Notes", type: "doc" },
            { name: "Intraoral Photo", type: "xray" }
        ]
    },
    {
        id: "CLM-9922",
        patient: "James T. Kirk",
        procedure: "D4341 - Periodontal Scaling",
        amount: "$240.00",
        attachments: [
            { name: "FMX Series", type: "xray" },
            { name: "Perio Chart", type: "doc" }
        ]
    }
];

export default ArielSlide;























